/**
 * PageFunnelTile — Admin dashboard tile showing which sections of
 * the app users actually visit each week.
 *
 * Powered by the `<PageHeroCompact>` / `<PageHero>` page-view beacon
 * — every migrated page emits a fire-and-forget POST to
 * `/api/analytics/page-view/track` on mount. This tile rolls those
 * up via `/api/admin/page-funnel?window_days=…` (alias of
 * `/api/analytics/page-view/summary`) and surfaces:
 *
 *   - Top section (theme) split — "Where is attention going?"
 *   - Top 10 visited pages — "What is the team using most?"
 *
 * Used during the UX migration to validate the new IA — if a section
 * is dead (zero visits over 14 days), it's a candidate for hiding
 * from the sidebar.
 *
 * Permission: org_admin or super_admin (backend gate).
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Activity, BarChart3 } from 'lucide-react';
import { useTelemetryTile } from '../../hooks';


// Themed colour mapping mirrors lib/sectionThemes.js so the bars
// match the same gradient family the user sees on each page.
const THEME_COLOR = {
  Maturity: 'bg-teal-500',
  LEI: 'bg-emerald-500',
  PE: 'bg-blue-500',
  ROE: 'bg-amber-500',
  Benchmarking: 'bg-purple-500',
  ImpactModelling: 'bg-indigo-500',
  Admin: 'bg-slate-600',
  Insights: 'bg-rose-500',
  Reengage: 'bg-cyan-500',
  Other: 'bg-slate-300',
};

// Phase colours mirror the home hero closed-loop pipeline (rose,
// blue, amber, emerald) so the entry-point bar reads as a literal
// echo of that hero strip.
const PHASE_COLOR = {
  IDENTIFY: 'bg-rose-500',
  MODEL: 'bg-blue-500',
  EXECUTE: 'bg-amber-500',
  PROVE: 'bg-emerald-500',
};


export default function PageFunnelTile() {
  const [windowDays, setWindowDays] = useState(7);
  const { data, loading, error } = useTelemetryTile({
    url: `/analytics/page-view/summary?window_days=${windowDays}`,
    deps: [windowDays],
  });

  if (error === 'forbidden') return null;

  const totals = data?.totals || { events: 0, distinct_pages: 0, distinct_users: 0 };
  const bySection = data?.by_section || [];
  const byPage = data?.by_page || [];
  const phaseEntryPoints = data?.phase_entry_points || [];
  const totalPhaseSessions = phaseEntryPoints.reduce((s, p) => s + (p.sessions || 0), 0);
  const loop = data?.loop_completion || { phase_active_users: 0, complete_users: 0, completion_pct: 0, by_phase_count: [] };
  const peakSection = bySection[0];

  return (
    <Card
      data-testid="page-funnel-tile"
      className="border-cyan-200 bg-gradient-to-br from-white to-cyan-50/40"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-cyan-600" />
              Page Funnel · Where users go
            </CardTitle>
            <CardDescription className="mt-0.5">
              Live visit counts for every migrated section. Powered by
              the page-hero mount beacon — surfaces dead routes and
              the most-loved sections of the IA.
            </CardDescription>
          </div>
          <div className="flex gap-1 shrink-0">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setWindowDays(d)}
                data-testid={`page-funnel-window-${d}d`}
                className={
                  'text-[11px] px-2 py-0.5 rounded-md border transition-colors ' +
                  (windowDays === d
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-cyan-300')
                }
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-xs text-slate-500" data-testid="page-funnel-loading">Loading…</p>
        ) : error ? (
          <p className="text-xs text-rose-600" data-testid="page-funnel-error">
            Couldn't load page funnel.
          </p>
        ) : totals.events === 0 ? (
          <p className="text-xs text-slate-500" data-testid="page-funnel-empty">
            No page views recorded yet for this window. The beacon
            fires on every <code className="bg-slate-100 text-[11px] px-1 rounded">PageHero</code>
            mount — visit any migrated page and refresh.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Headline stats */}
            <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
              <span data-testid="page-funnel-events">
                <strong className="text-slate-700">{totals.events.toLocaleString()}</strong> visits
              </span>
              <span aria-hidden="true">·</span>
              <span data-testid="page-funnel-pages">
                <strong className="text-slate-700">{totals.distinct_pages}</strong> pages
              </span>
              <span aria-hidden="true">·</span>
              <span data-testid="page-funnel-users">
                <strong className="text-slate-700">{totals.distinct_users}</strong> users
              </span>
              {peakSection && (
                <>
                  <span aria-hidden="true">·</span>
                  <span data-testid="page-funnel-peak-section">
                    Peak: <strong className="text-slate-700">{peakSection.theme}</strong> ({peakSection.share_pct}%)
                  </span>
                </>
              )}
              {data?.generated_at && (
                <>
                  <span aria-hidden="true">·</span>
                  <span
                    className="text-slate-400"
                    data-testid="page-funnel-generated-at"
                    title={data.generated_at}
                  >
                    Updated {new Date(data.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </>
              )}
            </div>

            {/* By Section bar — single horizontal stack */}
            <div data-testid="page-funnel-by-section">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                By Section
              </p>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                {bySection.map((row) => (
                  <div
                    key={row.theme}
                    title={`${row.theme}: ${row.count} (${row.share_pct}%)`}
                    style={{ width: `${row.share_pct}%` }}
                    className={THEME_COLOR[row.theme] || 'bg-slate-300'}
                    data-testid={`page-funnel-section-bar-${row.theme}`}
                  />
                ))}
              </div>
              <ul className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px]">
                {bySection.map((row) => (
                  <li
                    key={row.theme}
                    className="flex items-center gap-1.5"
                    data-testid={`page-funnel-section-row-${row.theme}`}
                  >
                    <span
                      className={`h-2 w-2 rounded-sm ${THEME_COLOR[row.theme] || 'bg-slate-300'}`}
                    />
                    <span className="text-slate-600 truncate">{row.theme}</span>
                    <span className="ml-auto tabular-nums text-slate-700 font-medium">
                      {row.count}
                    </span>
                    <span className="tabular-nums text-slate-400">{row.share_pct}%</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Phase Entry-Points — first phase a user lands on per
                day. Echoes the closed-loop hero on the home page. */}
            {totalPhaseSessions > 0 && (
              <div data-testid="page-funnel-phase-entry-points">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                  Closed-Loop Entry Points
                  <span className="ml-1.5 text-slate-400 font-normal normal-case tracking-normal">
                    · which phase users start in
                  </span>
                </p>
                <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
                  {phaseEntryPoints.map((p) => p.share_pct > 0 && (
                    <div
                      key={p.phase}
                      title={`${p.phase}: ${p.sessions} sessions (${p.share_pct}%)`}
                      style={{ width: `${p.share_pct}%` }}
                      className={PHASE_COLOR[p.phase] || 'bg-slate-300'}
                      data-testid={`page-funnel-phase-bar-${p.phase}`}
                    />
                  ))}
                </div>
                <ul className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 mt-2 text-[11px]">
                  {phaseEntryPoints.map((p) => (
                    <li
                      key={p.phase}
                      className="flex items-center gap-1.5"
                      data-testid={`page-funnel-phase-row-${p.phase}`}
                    >
                      <span className={`h-2 w-2 rounded-sm ${PHASE_COLOR[p.phase] || 'bg-slate-300'}`} />
                      <span className="text-slate-600 font-mono text-[10px] tracking-wider">{p.phase}</span>
                      <span className="ml-auto tabular-nums text-slate-700 font-medium">
                        {p.sessions}
                      </span>
                      <span className="tabular-nums text-slate-400">{p.share_pct}%</span>
                    </li>
                  ))}
                </ul>

                {/* Loop completion KPI — how many phase-active users
                    hit ALL FOUR phases this window. The "by phases
                    hit" breakdown shows the funnel drop-off. */}
                {loop.phase_active_users > 0 && (
                  <div
                    className="mt-3 pt-3 border-t border-slate-200/70"
                    data-testid="page-funnel-loop-completion"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[11px] text-slate-500">
                          <span
                            className="text-2xl font-extrabold text-slate-800 tabular-nums"
                            data-testid="page-funnel-loop-complete-count"
                          >
                            {loop.complete_users}
                          </span>
                          <span className="text-slate-400 mx-1">of</span>
                          <span
                            className="text-base font-semibold text-slate-700 tabular-nums"
                            data-testid="page-funnel-loop-active-count"
                          >
                            {loop.phase_active_users}
                          </span>
                          <span className="ml-1.5 text-slate-500">phase-active users completed the loop</span>
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          A "complete" user touched IDENTIFY → MODEL → EXECUTE → PROVE in this window.
                        </p>
                      </div>
                      <div
                        className={
                          'shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ' +
                          (loop.completion_pct >= 60
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : loop.completion_pct >= 25
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-rose-50 text-rose-700 border-rose-200')
                        }
                        data-testid="page-funnel-loop-completion-pct"
                      >
                        {loop.completion_pct}% loop-complete
                      </div>
                    </div>
                    {/* Drop-off ladder: how many users hit 4 / 3 / 2 / 1 phases. */}
                    <ul className="grid grid-cols-4 gap-2 mt-2.5" data-testid="page-funnel-loop-ladder">
                      {(loop.by_phase_count || []).map((rung) => (
                        <li
                          key={rung.phases_hit}
                          className="px-2 py-1.5 rounded-md bg-slate-50 border border-slate-100 text-center"
                          data-testid={`page-funnel-loop-rung-${rung.phases_hit}`}
                        >
                          <p className="text-[18px] font-bold text-slate-700 tabular-nums leading-none">
                            {rung.users}
                          </p>
                          <p className="text-[9.5px] text-slate-500 mt-1 uppercase tracking-wide">
                            {rung.phases_hit} {rung.phases_hit === 1 ? 'phase' : 'phases'}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* By Page list */}
            <div data-testid="page-funnel-by-page">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Top {Math.min(byPage.length, 10)} Pages
              </p>
              <ol className="space-y-1">
                {byPage.slice(0, 10).map((row, i) => (
                  <li
                    key={row.path}
                    className="flex items-center gap-2 px-2 py-1 rounded-md bg-white border border-slate-100 text-[12px]"
                    data-testid={`page-funnel-page-row-${i}`}
                  >
                    <span
                      className={`h-2 w-2 rounded-sm shrink-0 ${THEME_COLOR[row.theme] || 'bg-slate-300'}`}
                    />
                    <span className="text-slate-800 truncate flex-1">{row.title}</span>
                    <code className="text-[10.5px] text-slate-400 truncate max-w-[150px]">{row.path}</code>
                    <span className="tabular-nums text-slate-700 font-semibold ml-1.5 w-10 text-right">
                      {row.count}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
